import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { User } from "../../models/users";
import { Entry } from "../../models/entries";
import { EntryFinalResults } from "../../models/entryfinalresults";
import { api } from "../../api";
import { LinksToAuthors } from "../LinksToAuthors";
import { VoteJSON, Vote } from "../../models/vote";
import { VoteStars } from "../../Vote/VoteStars";
import {
  CompoCategoriesClient,
  CompoCategoriesJSON,
} from "../../../../src/entity/CompoCategoriesClient";
import { CategoryStat } from "./CategoryStat";
import { Page } from "../../DesignSystem/formatting/TwoColumnView";
import { RLink } from "../../DesignSystem/RLink";
import { ChartBarIcon } from "@heroicons/react/24/outline";

type EntryStatsParams = {
  id: string;
};

type PublicVote = VoteJSON & { username: string; avatar_url: string };

export const EntryStatsPage = (): JSX.Element => {
  const { id } = useParams<EntryStatsParams>();
  const [entry, setEntry] = useState<Entry>();
  const [entryResults, setEntryResults] = useState<EntryFinalResults>();
  const [categoryNames, setCategoryNames] = useState<CompoCategoriesJSON[]>();
  const [entryUsers, setEntryUsers] = useState<User[]>([]);
  const [publicVotes, setPublicVotes] = useState<PublicVote[]>([]);

  useEffect(() => {
    let cancelled = false;

    const fetchStats = async () => {
      const result = await api.get<"/api/entry/:id/stats">(
        `/api/entry/${id}/stats`
      );

      if (api.isToast(result) || cancelled) {
        return;
      }

      setEntry(new Entry(result.entry));
      setEntryResults(result.final_results);
      setCategoryNames(result.categories);
      setEntryUsers((result.entry_users || []).map((user) => new User(user)));
      setPublicVotes(result.public_votes || []);
    };

    fetchStats();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const categories = useMemo(() => {
    if (!categoryNames || !entry) {
      return undefined;
    }
    const matchingCategory = categoryNames.find(
      (category) => category.type === entry.type
    );
    return matchingCategory
      ? new CompoCategoriesClient(matchingCategory)
      : undefined;
  }, [categoryNames, entry]);

  if (!entry || !entryResults || !categories) {
    return (
      <Page>
        <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
          <ChartBarIcon className="h-5 w-5 animate-pulse" />
          Loading Stats...
        </div>
      </Page>
    );
  }

  const categoryCount = categories.list.length;

  const categoryStats = [
    {
      name: categories.list[0],
      standing: entryResults.category_1_standing,
      score: entryResults.category_1,
      histogram: entryResults.category_1_histo,
    },
    {
      name: categories.list[1],
      standing: entryResults.category_2_standing,
      score: entryResults.category_2,
      histogram: entryResults.category_2_histo,
    },
    {
      name: categories.list[2],
      standing: entryResults.category_3_standing,
      score: entryResults.category_3,
      histogram: entryResults.category_3_histo,
    },
    {
      name: categories.list[3],
      standing: entryResults.category_4_standing,
      score: entryResults.category_4,
      histogram: entryResults.category_4_histo,
    },
    {
      name: categories.list[4],
      standing: entryResults.category_5_standing,
      score: entryResults.category_5,
      histogram: entryResults.category_5_histo,
    },
    {
      name: categories.list[5],
      standing: entryResults.category_6_standing,
      score: entryResults.category_6,
      histogram: entryResults.category_6_histo,
    },
    {
      name: categories.list[6],
      standing: entryResults.category_7_standing,
      score: entryResults.category_7,
      histogram: entryResults.category_7_histo,
    },
    {
      name: categories.list[7],
      standing: entryResults.category_8_standing,
      score: entryResults.category_8,
      histogram: entryResults.category_8_histo,
    },
    {
      name: categories.list[8],
      standing: entryResults.category_9_standing,
      score: entryResults.category_9,
      histogram: entryResults.category_9_histo,
    },
  ].filter((stat) => stat.name);

  return (
    <Page>
      <div className="mb-8">
        <div className="mb-2 flex items-center gap-3">
          <ChartBarIcon className="h-8 w-8 text-slate-400 dark:text-slate-500" />
          <h1 className="text-3xl font-bold text-slate-800 dark:text-white">
            {entry.title}
          </h1>
        </div>
        <p className="text-slate-500 dark:text-slate-400">
          by <LinksToAuthors users={entryUsers} /> | Stats Breakdown
        </p>
        <RLink
          to={`/entry/${entry.id}`}
          className="mt-2 inline-block text-sm text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300"
        >
          &larr; Back to entry
        </RLink>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {categoryStats.map((stat, index) => (
          <CategoryStat
            key={stat.name || index}
            categoryName={stat.name}
            categoryStanding={stat.standing}
            categoryScore={stat.score}
            categoryHistogram={stat.histogram}
          />
        ))}
      </div>

      {publicVotes.length > 0 && (
        <div className="mt-8 rounded-2xl border border-slate-200 bg-white/90 px-6 py-5 shadow-sm backdrop-blur-sm dark:border-slate-700 dark:bg-slate-800 dark:shadow-none">
          <h2 className="mb-4 text-2xl font-bold text-slate-800 dark:text-white">
            Individual Scores
          </h2>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="pb-3 pr-4 text-left text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">
                    Voter
                  </th>
                  {new CompoCategoriesClient(categories).list.map((name) => (
                    <th
                      key={name}
                      className="pb-3 px-2 text-center text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500"
                    >
                      {name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {publicVotes.map((voteJson) => {
                  const vote = new Vote(voteJson);

                  return (
                    <tr
                      key={
                        voteJson.user_id ||
                        `${voteJson.username}-${voteJson.entry_id || "vote"}`
                      }
                    >
                      <td className="py-3 pr-4 text-sm font-medium text-slate-700 dark:text-slate-300">
                        {voteJson.username}
                      </td>
                      {vote.votes
                        .slice(0, categoryCount)
                        .map((score, index) => (
                          <td
                            key={`${voteJson.username}-${index}`}
                            className="py-3 px-2 text-center"
                          >
                            <VoteStars score={score} />
                          </td>
                        ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Page>
  );
};
